import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { getSupabase } from '@/lib/supabase';
import { useAuthReady } from '@/hooks/useAuthReady';
import type { Unit, CreateUnitInput } from '@/types/database';
import { propertyKeys } from './useProperties';
import { checkCanAddUnit } from '@/lib/stripe';
import { subscriptionKeys } from './useSubscription';

// Query keys
export const unitKeys = {
  all: ['units'] as const,
  lists: () => [...unitKeys.all, 'list'] as const,
  listByProperty: (propertyId: string) =>
    [...unitKeys.lists(), 'property', propertyId] as const,
  listByLandlord: (landlordId: string) =>
    [...unitKeys.lists(), 'landlord', landlordId] as const,
  details: () => [...unitKeys.all, 'detail'] as const,
  detail: (id: string) => [...unitKeys.details(), id] as const,
};

// Fetch all units for a property
export function useUnitsByProperty(propertyId: string | undefined) {
  const { isReady } = useAuthReady();

  return useQuery({
    queryKey: unitKeys.listByProperty(propertyId ?? ''),
    queryFn: async (): Promise<Unit[]> => {
      if (!propertyId) return [];

      const { data, error } = await getSupabase()
        .from('units')
        .select('*')
        .eq('property_id', propertyId)
        .is('deleted_at', null)
        .order('name', { ascending: true });

      if (error) throw error;
      return data ?? [];
    },
    enabled: !!propertyId && isReady,
  });
}

// Fetch all units for current user (across all properties)
export function useAllUnits() {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: unitKeys.listByLandlord(userId ?? ''),
    queryFn: async (): Promise<(Unit & { property_name: string })[]> => {
      if (!userId) return [];

      // First get user's properties
      const { data: properties, error: propError } = await getSupabase()
        .from('properties')
        .select('id, name')
        .eq('landlord_id', userId)
        .is('deleted_at', null);

      if (propError) throw propError;
      if (!properties?.length) return [];

      const propertyIds = properties.map((p) => p.id);
      const propertyMap = new Map(properties.map((p) => [p.id, p.name]));

      // Then get units for those properties
      const { data: units, error: unitError } = await getSupabase()
        .from('units')
        .select('*')
        .in('property_id', propertyIds)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (unitError) throw unitError;

      return (units ?? []).map((unit) => ({
        ...unit,
        property_name: propertyMap.get(unit.property_id) ?? '',
      }));
    },
    enabled: isReady,
  });
}

/**
 * Id of a whole property's single main unit, or null. A DB trigger
 * (create_main_unit_for_whole_property, AFTER INSERT on properties) creates it,
 * so callers must UPDATE that unit instead of inserting a second "main" unit.
 */
export async function findMainUnitId(propertyId: string): Promise<string | null> {
  const { data, error } = await getSupabase()
    .from('units')
    .select('id')
    .eq('property_id', propertyId)
    .eq('is_main_unit', true)
    .is('deleted_at', null)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as { id: string } | null)?.id ?? null;
}

// Fetch single unit by ID
export function useUnit(id: string | undefined) {
  const { isReady } = useAuthReady();

  return useQuery({
    queryKey: unitKeys.detail(id ?? ''),
    queryFn: async (): Promise<Unit | null> => {
      if (!id) return null;

      const { data, error } = await getSupabase()
        .from('units')
        .select('*')
        .eq('id', id)
        .is('deleted_at', null)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        throw error;
      }
      return data;
    },
    enabled: !!id && isReady,
  });
}

// Create unit mutation with subscription limit check
export function useCreateUnit() {
  const queryClient = useQueryClient();
  const { user } = useUser();

  return useMutation({
    mutationFn: async (input: CreateUnitInput): Promise<Unit> => {
      // Check subscription limit before creating
      if (user?.id) {
        const limitInfo = await checkCanAddUnit(user.id);
        // A failed check is "unknown", not "0 allowed" — let the insert run;
        // the database trigger enforces the real limit either way.
        if (!limitInfo.allowed && limitInfo.subscriptionStatus !== 'error') {
          throw new Error(
            `UNIT_LIMIT_REACHED:${limitInfo.currentUnits}:${limitInfo.maxUnits}`
          );
        }
      }

      const { data, error } = await getSupabase()
        .from('units')
        .insert({
          ...input,
          cleaning_fee: input.cleaning_fee ?? 0,
          amenities: input.amenities ?? [],
          images: [],
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: unitKeys.listByProperty(data.property_id),
      });
      queryClient.invalidateQueries({ queryKey: unitKeys.lists() });
      // The calendars and the occupancy card list units too.
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['seasonalOccupancy'] });
      // Also invalidate property detail (might show unit count)
      queryClient.invalidateQueries({
        queryKey: propertyKeys.detail(data.property_id),
      });
      // Invalidate subscription/unit limit queries
      if (user?.id) {
        queryClient.invalidateQueries({
          queryKey: subscriptionKeys.canAddUnit(user.id),
        });
        queryClient.invalidateQueries({
          queryKey: subscriptionKeys.billing(user.id),
        });
      }
    },
  });
}

// Update unit mutation
export function useUpdateUnit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<Unit> & { id: string }): Promise<Unit> => {
      const { data, error } = await getSupabase()
        .from('units')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: unitKeys.listByProperty(data.property_id),
      });
      queryClient.invalidateQueries({ queryKey: unitKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: unitKeys.lists() });
      // Base price / active flag show in the calendars.
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      // Active units count against the plan.
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
}

// Soft delete unit mutation (moves to trash for 30 days)
// Blocks if unit has active bookings.
export interface DeleteUnitResult {
  ok: boolean;
  error_code?: 'HAS_ACTIVE_BOOKINGS' | 'FORBIDDEN' | 'NOT_FOUND';
  error_message?: string;
}

export function useDeleteUnit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
    }: {
      id: string;
      propertyId: string;
    }): Promise<DeleteUnitResult> => {
      const { data, error } = await getSupabase().rpc('soft_delete_unit', {
        p_unit_id: id,
      });
      if (error) throw error;

      const result = data as DeleteUnitResult;
      if (!result.ok) {
        const err = new Error(result.error_message || result.error_code || 'Delete failed') as Error & {
          code?: string;
          result?: DeleteUnitResult;
        };
        err.code = result.error_code;
        err.result = result;
        throw err;
      }
      return result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: unitKeys.listByProperty(variables.propertyId),
      });
      queryClient.invalidateQueries({ queryKey: unitKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['trash'] });
      // The calendars and the occupancy card list units too.
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      queryClient.invalidateQueries({ queryKey: ['seasonalOccupancy'] });
      // A trashed unit no longer counts against the plan.
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
}

// Toggle unit active status
export function useToggleUnitStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      isActive,
    }: {
      id: string;
      isActive: boolean;
    }): Promise<Unit> => {
      const { data, error } = await getSupabase()
        .from('units')
        .update({
          is_active: isActive,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: unitKeys.listByProperty(data.property_id),
      });
      queryClient.invalidateQueries({ queryKey: unitKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: unitKeys.lists() });
      // Base price / active flag show in the calendars.
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      // Active units count against the plan.
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });
}
